import { useEffect, useRef } from 'react';

// Keyboard-slam detector: a palm slammed on the keyboard (or a violent grab) registers many
// distinct keys at once, far beyond shortcuts (~4 with modifiers) or gaming holds (~5).
export const HELD_KEYS_THRESHOLD = 6; // simultaneous distinct keys — also the common 6KRO cap
export const BURST_DISTINCT_KEYS = 8; // distinct keys in the window ≈ 13/s, beyond human typing
export const BURST_WINDOW_MS = 300;

export const useKeyboardPanic = (callback, { enabled = true } = {}) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    let triggered = false;
    const held = new Set();
    let burst = [];

    const detach = () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('keyup', onKeyUp, { capture: true });
      window.removeEventListener('blur', clear);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    const trigger = (value, reason) => {
      triggered = true;
      detach();
      callbackRef.current(value, reason);
    };

    const onKeyDown = (event) => {
      if (triggered || event.repeat) return;

      held.add(event.code);

      const now = Date.now();
      burst = burst.filter((entry) => now - entry.at < BURST_WINDOW_MS);
      burst.push({ code: event.code, at: now });
      const distinct = new Set(burst.map((entry) => entry.code)).size;

      if (held.size >= HELD_KEYS_THRESHOLD) {
        trigger(held.size, 'held');
      } else if (distinct >= BURST_DISTINCT_KEYS) {
        trigger(distinct, 'burst');
      }
    };

    const onKeyUp = (event) => held.delete(event.code);

    // Focus loss can swallow keyups (e.g. macOS Cmd combos) — drop stale state instead of
    // letting phantom held keys accumulate toward a false trigger.
    const clear = () => {
      held.clear();
      burst = [];
    };

    const onVisibilityChange = () => {
      if (document.hidden) clear();
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('keyup', onKeyUp, { capture: true });
    window.addEventListener('blur', clear);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return detach;
  }, [enabled]);
};
